import React, { useState, useEffect, useRef, useContext } from 'react';
import ConversationActions from './ConversationActions';
import useStreamHandler from 'utils/useStreamHandler';
import {MemoModalProvider} from 'projects/components/modal/MemoModalProvider';
import TranscriptCodePageContainer from 'projects/components/TranscriptCodePageContainer'
import ChatInput from './ChatInput';
import {BetaModalProvider} from 'projects/components/modal/BetaModalProvider';
import { Redirect } from 'react-router-dom';
import CodeableContext from 'contexts/CodeableContext';
import CodeOperationsContext from 'contexts/CodeOperationsContext';
import RequiresExcerptsOnboardingWrapper from 'projects/components/RequiresExcerptsOnboardingWrapper';
import { FilterStateProvider } from 'contexts/FilterStateContext';
import MessageList from './MessageList';
import ProjectChatBetaModal from './ProjectChatBetaModal';
import { useProjectWithErrorHandling } from 'hooks/projects/useProjectWithErrorHandling';
import aiChatImage from 'images/onboarding/onboarding_ai.png';

const ProjectChatContainer = (props) => {
  // Check if project exists and handle 404
  const { NotFoundComponent } = useProjectWithErrorHandling(props.projectID);
  
  const [messages, setMessages] = useState([]);
  const [streamConfig, setStreamConfig] = useState({ url: null, headers: null });
  const { data: streamData } = useStreamHandler(streamConfig.url, streamConfig.headers);

  const {
    selectedExcerptId,
    onDeselect
  } = useContext(CodeableContext);
  
  const {
    onExcerptSelected
  } = useContext(CodeOperationsContext);

  const messagesEndRef = useRef(null);

  const addMessageWithoutDuplicates = (newMessage) => {
    setMessages(prevMessages => {
      if (prevMessages.find(m => m.id === newMessage.id)) return prevMessages;
      return [...prevMessages, newMessage];
    })
  }

  const scrollToBottom = () => {
    if ( !messagesEndRef ) return;
    if ( !messagesEndRef.current ) return;
    messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }

  const getConversationID = (props) => {
    return props.match && props.match.params ? props.match.params.conversationID : null;
  }

  const loadConversation = (props) => {
    const conversationID = getConversationID(props);
    if (!conversationID) {
      setMessages([]);
    } else {
      ConversationActions.getConversation(conversationID)
      .then((response) => {
        const conversation = response.data || {messages: []};
        const sortedMessages = conversation.messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setMessages(sortedMessages);

        const conversationProjectID = conversation.project_id;
        if (!!conversationProjectID && conversationProjectID !== props.projectID) {
          props.history.push({
            pathname: `/projects/${conversationProjectID}/chat/${conversationID}`,
            search: props.location.search
          });
        }
      })
    }
  }

  const createAIConversationAndUpdateState = (conversationID, filters) => {
    const projectID = props.projectID;

    if (!projectID) return;

    ConversationActions.createAIConversation(projectID, conversationID, filters).then((response) => {
      const data = response.data || {};
      const message = data.message || {};
      const stream = data.stream || {};

      // if message id is already in messages, no need to do anything
      if (messages.find(m => m.id === message.id)) return;
  
      addMessageWithoutDuplicates(message);

      setStreamConfig({
        url: stream.stream_url,
        headers: data.auth,
        message_id: message.id
      });    
    });
  }
  

  const onSubmit = (chatText, filters) => {
    const conversationID = getConversationID(props);

    // TODO: this is a bit hacky
    // if streamConfig.message_id is not null, then save the streamData to corresponding message in messages
    if (streamConfig.message_id) {
      setMessages(prevMessages => {
        const newMessages = [...prevMessages];
        const messageIndex = newMessages.findIndex(message => message.id === streamConfig.message_id);
        if (messageIndex > -1) {
          newMessages[messageIndex].content = streamData;
        }
        return newMessages;
      });
    }

    if (conversationID) {
      ConversationActions.createMessage(conversationID, chatText).then((response) => {
        addMessageWithoutDuplicates(response.data);
        createAIConversationAndUpdateState(conversationID, filters);
      })
    } else {
      ConversationActions.createConversation(props.projectID, chatText).then((response) => {
        setMessages(response.data.messages);
        props.history.push({
          pathname: `/projects/${props.projectID}/chat/${response.data.id}`,
          search: props.location.search
        });
        createAIConversationAndUpdateState(response.data.id, filters);
      })  
    }
  }

  useEffect(() => {
    loadConversation(props);

    setTimeout(() => {
      onDeselect();
    }, 0);
  }, [props.match.params.conversationID]); // Only re-run the effect if props.chatID changes

  useEffect(scrollToBottom, [messages, streamData]);

  const onRejectTerms = () => {
    props.history.push(`/projects/${props.projectID}`);
  }

  if ( !process.env['AI_FEATURE_FLAG'] && props.projectID ) {
    return <Redirect to={`/projects/${props.projectID}`} />
  }

  if (NotFoundComponent) {
    return <NotFoundComponent />;
  }

  return (
    <RequiresExcerptsOnboardingWrapper
      loaded={props.onboardingState.loaded}
      showOnboarding={props.onboardingState.hasNoCodeableData}
      hasExcerpts={props.onboardingState.hasExcerpts}
      header="Feel supported with Delve's AI as your peer debriefer."
      body="Ask questions about your coded snippets."
      secondBody="First, code a transcript."
      projectID={props.projectID}
      hasEditPermission={props.hasEditPermission}
      helpArticleText="Learn more about AI chat"
      intercomArticleId={process.env['INTERCOM_ARTICLE_AI_FEATURES']}
      mediaType="image"
      mediaSrc={aiChatImage}
    >
      <BetaModalProvider onReject={onRejectTerms}>
        <ProjectChatBetaModal/>
        <MemoModalProvider>
          <TranscriptCodePageContainer
            projectID={props.projectID}
            hasEditPermission={props.hasEditPermission}
          />

          <div style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%' }}
                className='chat'
                onClick={() => onDeselect()}>
            <div style={{ flexGrow: 1, overflowY: 'auto' }}>
              <MessageList
                messages={messages}
                projectID={props.projectID}
                hasEditPermission={props.hasEditPermission}
                selectedExcerptId={selectedExcerptId}
                streamConfig={streamConfig}
                streamData={streamData}
                onExcerptSelected={onExcerptSelected}
              />
              <div ref={messagesEndRef} /> 
            </div>
            <FilterStateProvider projectID={props.projectID}>
              <ChatInput
                projectID={props.projectID}
                onSubmit={onSubmit}
              />
            </FilterStateProvider>
          </div>
        </MemoModalProvider>
      </BetaModalProvider>
    </RequiresExcerptsOnboardingWrapper>
  )
}

export default ProjectChatContainer;
